import {
  Avatar,
  MessageBar,
  MessageBarBody,
  Spinner,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { useAadGroupMembers } from '@/hooks/useVisibility';
import { formatMailDate } from '@/utils/mailDate';

const useStyles = makeStyles({
  wrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    // Décalage vers la droite + filet : on lit tout de suite que ces personnes
    // appartiennent au groupe au-dessus, et non à la liste principale.
    marginLeft: tokens.spacingHorizontalL,
    paddingLeft: tokens.spacingHorizontalS,
    borderLeft: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
    maxHeight: '160px',
    overflowY: 'auto',
    minWidth: 0,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    minWidth: 0,
  },
  texts: { display: 'flex', flexDirection: 'column', minWidth: 0, flexGrow: 1 },
  name: {
    fontSize: tokens.fontSizeBase200,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  secondary: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  stale: { fontSize: tokens.fontSizeBase100, color: tokens.colorNeutralForeground3 },
  hint: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 },
  loading: { display: 'flex', padding: tokens.spacingVerticalXS },
});

interface Props {
  groupId: string;
}

/**
 * Composition nominative d'un groupe Entra.
 *
 * Exigence produit : quand on accorde l'accès à un groupe, on doit pouvoir voir
 * QUI cela représente. Le composant n'est monté que lorsque la liste est
 * dépliée — c'est ce montage qui déclenche la resynchronisation best-effort
 * côté serveur.
 */
export function AadGroupMemberList({ groupId }: Props) {
  const styles = useStyles();
  const members = useAadGroupMembers(groupId);

  if (members.isPending) {
    return (
      <div className={styles.wrap}>
        <div className={styles.loading}>
          <Spinner size="tiny" label="Chargement des membres…" />
        </div>
      </div>
    );
  }

  if (members.error) {
    return (
      <div className={styles.wrap}>
        <MessageBar intent="error">
          <MessageBarBody>Membres indisponibles : {members.error.message}</MessageBarBody>
        </MessageBar>
      </div>
    );
  }

  const data = members.data;
  if (!data) return null;

  return (
    <div className={styles.wrap}>
      {/* `stale` = la resynchronisation Entra a échoué, on montre le cache. On le
          signale sans dramatiser : la liste reste exploitable. */}
      {data.stale && (
        <Text className={styles.stale}>
          Liste issue du cache
          {data.syncedAt ? ` (dernière synchronisation : ${formatMailDate(data.syncedAt)})` : ''}.{' '}
          {data.syncError ?? 'Resynchronisation impossible pour le moment.'}
        </Text>
      )}

      {data.members.length === 0 ? (
        <Text className={styles.hint}>Ce groupe ne contient aucun membre.</Text>
      ) : (
        data.members.map((m, i) => (
          // Un membre peut ne pas avoir d'e-mail exploitable : on complète la clé
          // par l'index pour rester stable sans inventer d'identifiant.
          <div key={m.email ?? `${data.groupId}#${i}`} className={styles.row}>
            <Avatar
              name={m.displayName}
              image={m.photoUrl ? { src: m.photoUrl } : undefined}
              color="colorful"
              size={20}
            />
            <span className={styles.texts}>
              <Text className={styles.name} title={m.fonction ?? m.displayName}>
                {m.displayName}
              </Text>
              {/* L'e-mail n'est plus affiché (on identifie par nom + photo). En
                  revanche `knownInPrism: false` — prestataire, départ — reste
                  signalé : le droit d'accès concerne bel et bien cette personne,
                  et c'est la seule façon de comprendre pourquoi elle n'a ni
                  photo ni fonction. */}
              {!m.knownInPrism && <Text className={styles.secondary}>Hors annuaire</Text>}
            </span>
          </div>
        ))
      )}
    </div>
  );
}
